import React, { memo, useEffect } from 'react'
import { Col, Form, message, Card, Button } from 'antd'
import PropTypes from 'prop-types'
import CustomRow from '../../../../reusable-components/CustomRow'
import PatientDetailsSection from './form-sections/PatientDetailsSection'
import ContactDetailsSection from './form-sections/ContactDetailsSection'
import AddressDetailsSection from './form-sections/AddressDetailsSection'
import MedicalAidSection from './form-sections/MedicalAidSection'
import dayjs from 'dayjs'
import { hexToRGBA } from '../../../../lib/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave } from '@fortawesome/free-solid-svg-icons'

const PatientProfileTab = memo(
  ({
    userData,
    form,
    loading,
    handleSubmit,
    token,
    isExamination,
    refreshView,
    closeTab,
    countries,
    countriesLoading,
    residentVisitor,
    setResidentVisitor,
    setCountry,
    countryObject,
    schemeLoading,
    schemeData,
    debouncedSchemeFilter,
    plansLoading,
    plansData,
    handleGetMedicalAidPlans,
    planOptionsLoading,
    planOptionData,
    handleGetMedicalAidPlanOptions,
    specialFormState,
    setSpecialFormState,
    setSchemeData,
    setPlansData,
    setPlanOptionData
  }) => {
    // Card styles for consistent appearance
    const cardStyle = {
      marginBottom: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.09)',
      border: `1px solid ${hexToRGBA(token.colorBorder, 0.5)}`
    }

    // Add a useEffect to ensure the date is set correctly
    useEffect(() => {
      if (userData && userData._id) {
        // First check if there's a date in userData
        if (userData.dateOfBirth) {
          try {
            // Try to parse the date using dayjs
            const dateObj = dayjs(userData.dateOfBirth)
            if (dateObj.isValid()) {
              // Force update the form field with the parsed date
              setTimeout(() => {
                // Set the field value
                form.setFieldValue('dateOfBirth', dateObj)

                // Force a form refresh to ensure the UI updates
                form.validateFields(['dateOfBirth']).catch(() => {})

                // Manually trigger a form change event to ensure components update
                try {
                  const event = new Event('change', { bubbles: true })
                  const datePickerInput = document.querySelector('input[placeholder="Select date"]')
                  if (datePickerInput) {
                    datePickerInput.dispatchEvent(event)
                  }
                } catch (error) {
                  // Silently handle any errors
                }
              }, 100) // Small delay to ensure form is ready
            } else {
              // Try to get the date from localStorage as a fallback
              tryLoadDateFromLocalStorage()
            }
          } catch (error) {
            // Try to get the date from localStorage as a fallback
            tryLoadDateFromLocalStorage()
          }
        } else {
          // Try to get the date from localStorage as a fallback
          tryLoadDateFromLocalStorage()
        }
      }
    }, [userData, form])

    // Function to try loading the date from localStorage
    const tryLoadDateFromLocalStorage = () => {
      if (userData && userData._id) {
        const storedDate = localStorage.getItem(`patient_dob_${userData._id}`)
        if (storedDate) {
          // Force update the form field with the stored date
          setTimeout(() => {
            // Set the field value
            form.setFieldValue('dateOfBirth', dayjs(storedDate))

            // Force a form refresh to ensure the UI updates
            form.validateFields(['dateOfBirth']).catch(() => {})

            // Manually trigger a form change event to ensure components update
            try {
              const event = new Event('change', { bubbles: true })
              const datePickerInput = document.querySelector('input[placeholder="Select date"]')
              if (datePickerInput) {
                datePickerInput.dispatchEvent(event)
              }
            } catch (error) {
              // Silently handle any errors
            }
          }, 100) // Small delay to ensure form is ready
        }
      }
    }

    // Add a function to manually check and update the date field
    const ensureDateFieldIsSet = () => {
      const currentValue = form.getFieldValue('dateOfBirth')

      if (!currentValue) {
        // Try localStorage first
        if (userData && userData._id) {
          const storedDate = localStorage.getItem(`patient_dob_${userData._id}`)
          if (storedDate) {
            form.setFieldValue('dateOfBirth', dayjs(storedDate))
            return
          }
        }

        // Then try userData
        if (userData && userData.dateOfBirth) {
          const dateObj = dayjs(userData.dateOfBirth)
          if (dateObj.isValid()) {
            form.setFieldValue('dateOfBirth', dateObj)
          }
        }
      }
    }

    // Call the function after a short delay to ensure the form is fully initialized
    useEffect(() => {
      const timer = setTimeout(() => {
        ensureDateFieldIsSet()
      }, 500)

      return () => clearTimeout(timer)
    }, [])

    return (
      <CustomRow gutter={[16, 16]}>
        <Col span={24}>
          <Form
            onFinishFailed={(e) => {
              message.error(e.errorFields[0].errors[0])
            }}
            onFinish={handleSubmit}
            disabled={loading}
            form={form}
            layout='vertical'
            initialValues={{
              ...userData,
              phoneNumber: userData.phoneNumber ? userData.phoneNumber.replace('+27', '') : null,
              dateOfBirth: userData.dateOfBirth ? dayjs(userData.dateOfBirth) : null,
              residentVisitor: residentVisitor,
              medicalAid: {
                name: userData.medicalAid?.name ? userData.medicalAid.name : undefined,
                schemeCode: userData.medicalAid?.schemeCode ? userData.medicalAid.schemeCode : undefined,
                plan: userData.medicalAid?.plan ? userData.medicalAid.plan : undefined,
                planCode: userData.medicalAid?.planCode ? userData.medicalAid.planCode : undefined,
                planOption: userData.medicalAid?.planOption ? userData.medicalAid.planOption : undefined,
                planOptionCode: userData.medicalAid?.planOptionCode ? userData.medicalAid.planOptionCode : undefined,
                number: userData.medicalAid?.number ? userData.medicalAid.number : '',
                dependantNumber: userData.medicalAid?.dependantNumber ? userData.medicalAid.dependantNumber : ''
              }
            }}
            preserve={false}
          >
            <CustomRow gutter={[16, 16]}>
              <Col xs={24} sm={24} md={24} lg={24}>
                <CustomRow gutter={[16, 16]}>
                  <Col xs={24} sm={24} md={14} lg={14}>
                    {/* Patient Details Card */}
                    <Card
                      title={<span style={{ fontWeight: 600, color: token.colorPrimary }}>Patient Details</span>}
                      style={cardStyle}
                      headStyle={{
                        backgroundColor: hexToRGBA(token.colorPrimary, 0.05),
                        borderBottom: `1px solid ${hexToRGBA(token.colorPrimary, 0.1)}`
                      }}
                      bodyStyle={{ padding: '16px' }}
                    >
                      <PatientDetailsSection
                        form={form}
                        specialFormState={specialFormState}
                        setSpecialFormState={setSpecialFormState}
                        token={token}
                      />
                    </Card>

                    {/* Contact Details Card */}
                    <Card
                      title={<span style={{ fontWeight: 600, color: token.colorPrimary }}>Contact Details</span>}
                      style={cardStyle}
                      headStyle={{
                        backgroundColor: hexToRGBA(token.colorPrimary, 0.05),
                        borderBottom: `1px solid ${hexToRGBA(token.colorPrimary, 0.1)}`
                      }}
                      bodyStyle={{ padding: '16px' }}
                    >
                      <ContactDetailsSection
                        form={form}
                        specialFormState={specialFormState}
                        setSpecialFormState={setSpecialFormState}
                        countryObject={countryObject}
                        token={token}
                        userData={userData}
                      />
                    </Card>

                    {/* Address Details Card */}
                    <Card
                      title={<span style={{ fontWeight: 600, color: token.colorPrimary }}>Address Details</span>}
                      style={cardStyle}
                      headStyle={{
                        backgroundColor: hexToRGBA(token.colorPrimary, 0.05),
                        borderBottom: `1px solid ${hexToRGBA(token.colorPrimary, 0.1)}`
                      }}
                      bodyStyle={{ padding: '16px' }}
                    >
                      <AddressDetailsSection
                        form={form}
                        token={token}
                        residentVisitor={residentVisitor}
                        setResidentVisitor={setResidentVisitor}
                        setCountry={setCountry}
                        countries={countries}
                        countriesLoading={countriesLoading}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={24} md={10} lg={10}>
                    {/* Medical Aid Card */}
                    <Card
                      title={<span style={{ fontWeight: 600, color: token.colorPrimary }}>Medical Aid</span>}
                      style={cardStyle}
                      headStyle={{
                        backgroundColor: hexToRGBA(token.colorPrimary, 0.05),
                        borderBottom: `1px solid ${hexToRGBA(token.colorPrimary, 0.1)}`
                      }}
                      bodyStyle={{ padding: '16px' }}
                    >
                      <MedicalAidSection
                        form={form}
                        hexToRGBA={hexToRGBA}
                        token={token}
                        residentVisitor={residentVisitor}
                        schemeData={schemeData}
                        schemeLoading={schemeLoading}
                        plansData={plansData}
                        plansLoading={plansLoading}
                        planOptionData={planOptionData}
                        planOptionsLoading={planOptionsLoading}
                        debouncedSchemeFilter={debouncedSchemeFilter}
                        setSchemeData={setSchemeData}
                        setPlansData={setPlansData}
                        setPlanOptionData={setPlanOptionData}
                        handleGetMedicalAidPlans={handleGetMedicalAidPlans}
                        handleGetMedicalAidPlanOptions={handleGetMedicalAidPlanOptions}
                        userData={userData}
                      />
                    </Card>

                    {/* Save Changes Button - Positioned below the Medical Aid card */}
                    <CustomRow justify='end' style={{ marginTop: 16 }}>
                      <Button
                        type='primary'
                        loading={loading}
                        onClick={() => form.submit()}
                        style={{
                          height: '40px',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          padding: '0 16px',
                          fontWeight: 500,
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                          background: '#f0f7ff',
                          borderColor: '#91caff',
                          color: '#0958d9'
                        }}
                        icon={<FontAwesomeIcon icon={faSave} style={{ color: '#0958d9' }} />}
                      >
                        Save Changes
                      </Button>
                    </CustomRow>
                  </Col>
                </CustomRow>
              </Col>
            </CustomRow>
          </Form>
        </Col>
      </CustomRow>
    )
  }
)

PatientProfileTab.propTypes = {
  userData: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  token: PropTypes.object.isRequired,
  isExamination: PropTypes.bool,
  refreshView: PropTypes.func.isRequired,
  closeTab: PropTypes.func.isRequired,
  countries: PropTypes.array.isRequired,
  countriesLoading: PropTypes.bool.isRequired,
  residentVisitor: PropTypes.string,
  setResidentVisitor: PropTypes.func.isRequired,
  setCountry: PropTypes.func.isRequired,
  countryObject: PropTypes.object,
  schemeLoading: PropTypes.bool.isRequired,
  schemeData: PropTypes.array.isRequired,
  debouncedSchemeFilter: PropTypes.func.isRequired,
  plansLoading: PropTypes.bool.isRequired,
  plansData: PropTypes.array.isRequired,
  handleGetMedicalAidPlans: PropTypes.func.isRequired,
  planOptionsLoading: PropTypes.bool.isRequired,
  planOptionData: PropTypes.array.isRequired,
  handleGetMedicalAidPlanOptions: PropTypes.func.isRequired,
  specialFormState: PropTypes.array.isRequired,
  setSpecialFormState: PropTypes.func.isRequired,
  setSchemeData: PropTypes.func.isRequired,
  setPlansData: PropTypes.func.isRequired,
  setPlanOptionData: PropTypes.func.isRequired
}

PatientProfileTab.displayName = 'PatientProfileTab'

export default PatientProfileTab
